import React from 'react';

import { FeatureDescriptor } from '@models/feature-descriptor';
import { Action } from '@utils/redux/models';
import { TableColumns } from '@models/column';

import { featureDescriptorEditorReducer } from './feature-descriptor-editor';
import {
  FeatureDescriptorEditorState,
  FeatureDescriptorDraft,
  AggregationDraft,
  FilterDraft,
  JoinDraft,
} from '../models';

export const FeatureDescriptorEditorContext = React.createContext<
  [FeatureDescriptorEditorState, React.Dispatch<Action>] | undefined
>(undefined);

export function useFeatureDescriptorEditorContext() {
  const context = React.useContext(FeatureDescriptorEditorContext);
  if (context === undefined) {
    throw new Error(
      'useFeatureDescriptorEditorContext must be used within a FeatureDescriptorEditorContextProvider'
    );
  }
  return context;
}

export function FeatureDescriptorEditorContextProvider(
  props: React.PropsWithChildren<{
    initFeatureDescriptor?: FeatureDescriptor;
    tableColumns: TableColumns;
  }>
) {
  const featureDescriptorDraft: FeatureDescriptorDraft = React.useMemo(() => {
    return props.initFeatureDescriptor
      ? {
          join: JoinDraft.fromJoin(
            props.initFeatureDescriptor?.join,
            props.tableColumns
          ),
          aggregation: AggregationDraft.fromAggregation(
            props.initFeatureDescriptor?.aggregation,
            props.tableColumns
          ),
          filter: FilterDraft.fromFilter(
            props.initFeatureDescriptor?.filter,
            props.tableColumns
          ),
        }
      : {
          join: {},
          filter: {},
          aggregation: {},
        };
  }, [props.initFeatureDescriptor]);

  const [state, dispatch] = React.useReducer(featureDescriptorEditorReducer, {
    ...featureDescriptorDraft,
    errors: {},
  });

  return (
    <FeatureDescriptorEditorContext.Provider
      value={[state, dispatch]}
      {...props}
    />
  );
}
